import { useMemo, useEffect, useState } from "react";
import { useWeather } from "../lib/useWeather";

// Admin-panel override key. When set, this wins over the real-weather
// reading so an admin can preview rain/snow/storm/etc. on a sunny day
// without faking their geolocation. null = use real weather.
const FX_OVERRIDE_KEY = "weatherFx:override:v1";

export function getWeatherFxOverride() {
  if (typeof localStorage === "undefined") return null;
  try {
    return localStorage.getItem(FX_OVERRIDE_KEY) || null;
  } catch { return null; }
}

export function setWeatherFxOverride(value) {
  if (typeof localStorage === "undefined") return;
  try {
    if (value) localStorage.setItem(FX_OVERRIDE_KEY, value);
    else       localStorage.removeItem(FX_OVERRIDE_KEY);
    // Broadcast so any mounted WeatherFX in this tab picks up the
    // change without a page reload. localStorage `storage` event
    // only fires across tabs, hence the custom event.
    window.dispatchEvent(new CustomEvent("weatherFx:override-changed"));
  } catch { /* ignore */ }
}

// Full-bleed weather overlay. Renders ambient particle effects keyed
// to the live `useWeather()` reading: rain streaks for rain/drizzle,
// snow flakes for snow, lightning flicker for storms. Pointer-events
// off so it never intercepts taps; z-index pinned just above the page
// background but well below all UI.
//
// Implementation is pure CSS animation on absolutely-positioned
// elements — no canvas, no WebGL, no per-frame JS. Each particle
// transforms via translate3d so the GPU compositor handles it; even
// 120 simultaneous drops costs essentially zero CPU.
//
// Density is tuned to match the *visual* feel of the condition, not
// realism — a true rainstorm is too dense to see through; we want
// "you can tell it's raining" without obscuring the calendar.

const COUNTS = {
  drizzle: 35,
  rain:    70,
  storm:   110,
  snow:    50,
};

// Cloud sprites — bigger, slower, fewer than rain particles, drifting
// HORIZONTALLY across the viewport rather than falling. Each sprite is
// a soft blurred ellipse — no hard edges, no SVG, just a radial-
// gradient fill plus filter:blur for puffiness.
//
// Distributed across the FULL viewport height (not just the top
// strip) so the user feels enveloped by overcast — clouds drifting
// at multiple horizons, not a thin band of decoration.
//
// Rain doesn't fall from a clear sky, so any wet condition (drizzle /
// rain / storm) gets clouds too — heavier with more severe weather.
// Storm caps the visual density just before the lightning veil takes
// over (more than ~14 clouds + lightning + rain crosses into noise).
const CLOUD_COUNTS = {
  drizzle: 6,
  rain:    10,
  storm:   14,
  cloudy:  9,
};

// Cloud sprite asset. Designed cumulus with three layered fills
// (charcoal base, white face, blue underside) at /public/img/weather-
// assets/cloud.svg. Replaces the multi-stop radial-gradient "blobs"
// — the SVG reads as an actual cloud silhouette instead of a fuzzy
// ellipse. Square 1254×1254 viewBox; cloud body fills roughly the
// middle 50% vertically, so we render with `backgroundSize: cover`
// inside a wider-than-tall box to crop the empty top/bottom.
const CLOUD_SVG_URL = "/img/weather-assets/cloud.svg";
// Aspect of the visible cloud body inside the SVG's square viewBox.
// Used to size each sprite's bounding box so cover-cropping lands the
// cloud body fully inside without empty padding above/below.
const CLOUD_ASPECT = 2.0;

function buildClouds(condition) {
  const count = CLOUD_COUNTS[condition] || 0;
  const out = [];
  for (let i = 0; i < count; i++) {
    const goesRight = Math.random() > 0.5; // half drift left→right, half right→left
    const w = 240 + Math.random() * 280; // 240-520px wide
    out.push({
      id: i,
      width:    w,
      height:   w / CLOUD_ASPECT, // tight box around the visible cloud body
      // Pinned to the upper strip of the viewport — clouds are
      // overhead, not floating in front of the user's content. 0-22%
      // from the top spreads them across roughly the first quarter
      // of the screen with light vertical layering.
      top:      -2  + Math.random() * 24,
      delay:    -Math.random() * 90,                          // negative so they're already mid-traverse on mount
      duration: 70  + Math.random() * 70,                     // 70-140s slow drift
      opacity:  0.75 + Math.random() * 0.20,                  // 0.75-0.95 — heavy and visible
      goesRight,
    });
  }
  return out;
}

// Atmospheric veil per condition, time-of-day aware. Daytime overcast
// is a flat, slightly-cool, mostly-bright sky (NOT dark) — only after
// sunset does cloudy weather actually feel night-dark. We lerp
// between a bright "day" tint and a heavy "night" tint by the
// dayFactor() below, so noon-cloudy is desaturated cool gray and
// midnight-cloudy is deep slate.
//
// Each entry: [r, g, b, alpha] at the day endpoint and the night
// endpoint. Multiply blend on every condition except snow (screen).
const VEIL_ENDPOINTS = {
  drizzle: { day: [140, 160, 185, 0.18], night: [40, 50, 75, 0.45] },
  rain:    { day: [120, 140, 170, 0.25], night: [30, 40, 65, 0.55] },
  storm:   { day: [60,  75,  105, 0.42], night: [15, 22, 45, 0.70] },
  cloudy:  { day: [130, 150, 180, 0.22], night: [35, 45, 70, 0.58] },
  snow:    { day: [240, 245, 255, 0.18], night: [200,215,235,0.28] },
  // Clear-sky veil — warm gold mid-day, deep amber-brown at sunset,
  // pulled darker through evening into night. The dayFactor lerp
  // walks day→night automatically; the day endpoint here is the
  // golden-hour tint that overlays the page when the sun is highest.
  clear:   { day: [255, 215, 130, 0.10], night: [40, 30, 60, 0.42] },
};

// Cloud body color endpoints — per-condition × time-of-day. The SVG
// asset (cloud.svg) has three layered fills:
//   • base (.st2)  — the silhouette / outline behind, sets cloud
//     "weight". Visible around the cloud's perimeter and below the
//     face — reads as both outline AND bottom-shadow.
//   • face (.st1)  — the top-lit cumulus surface
//   • under (.st0) — the cooler ambient-bounce underside detail
// All three shift per state. The original asset's near-black navy
// base (#00374b) was retained for cloudy/day in the first pass, which
// made the outline look identical to the static asset — bug. Now the
// daytime bases sit in the mid-slate range so the outline visibly
// retreats during a softer overcast, and stretches into deep slate
// during storms. Night endpoints push everything toward charcoal.
const CLOUD_PALETTES = {
  // Border (base) is now VERY LIGHT — only a hair darker than the
  // face so it reads as a subtle outline, not a heavy ring. The
  // weight of each state still comes from:
  //   • face — sets the dominant body color per state
  //   • under — the darker front shadow detail (kept relatively
  //     deep so cloud bottoms still feel weighty)
  // Storm gets a LOWER face (it's the dark-gray-cloud state) but
  // its base is still only slightly darker than its face — light
  // border discipline applies across the board.
  cloudy: {
    day:   { base: [200, 198, 208], face: [240, 240, 246], under: [110, 112, 130] },
    night: { base: [78, 80, 92],    face: [120, 122, 135], under: [42, 48, 62]    },
  },
  rain: {
    day:   { base: [180, 195, 215], face: [230, 240, 250], under: [70, 110, 150]  },
    night: { base: [70, 85, 115],   face: [110, 125, 150], under: [30, 55, 88]    },
  },
  storm: {
    // Storm = pure neutral gray, no hue. Face sits at mid-dark gray
    // so the cloud reads as a heavy rain-bearing mass. Border now
    // sits only ~15 units below the face — a barely-there soft
    // edge, matching how cloudy / rain / drizzle handle their
    // outlines. Under stays much darker so the cloud still has
    // weight at the bottom.
    day:   { base: [110, 110, 112], face: [128, 128, 130], under: [50, 50, 52]    },
    night: { base: [38, 38, 40],    face: [55, 55, 57],    under: [18, 18, 20]    },
  },
  drizzle: {
    day:   { base: [188, 198, 215], face: [232, 238, 246], under: [100, 122, 148] },
    night: { base: [80, 92, 115],   face: [122, 132, 152], under: [48, 62, 88]    },
  },
  snow: {
    day:   { base: [218, 232, 250], face: [252, 254, 255], under: [135, 175, 222] },
    night: { base: [130, 158, 198], face: [195, 215, 235], under: [70, 100, 145]  },
  },
};

// Drop-shadow approach removed — `drop-shadow()` filter (especially
// stacked twice per cloud × 6-14 clouds) torches the GPU compositor
// on every animation frame because the browser has to re-rasterize
// the SVG into a shadow buffer + blur it on each tick. The cloud's
// own three-layer SVG (base silhouette behind face + under) carries
// enough state-tinted contrast to read without a CSS shadow.

// Lookup helper — returns the three per-fill rgb triplets for the
// current condition + time-of-day. Falls back to the cloudy palette
// for unknown conditions.
function cloudFillsFor(condition, t) {
  const pal = CLOUD_PALETTES[condition] || CLOUD_PALETTES.cloudy;
  return {
    base:  lerpRgb(pal.day.base,  pal.night.base,  1 - t),
    face:  lerpRgb(pal.day.face,  pal.night.face,  1 - t),
    under: lerpRgb(pal.day.under, pal.night.under, 1 - t),
  };
}
function rgbStr([r, g, b]) { return `rgb(${r}, ${g}, ${b})`; }

// Inline cloud SVG — same path data as /public/img/weather-assets/
// cloud.svg, just with the three fills wired to props instead of
// CSS classes so we can drive them per-condition. Inlined (not <img>)
// because CSS filters can't recolor a multi-fill SVG precisely; the
// path data is small (~1.6 KB) and embedded once at module load.
function CloudSvg({ baseFill, faceFill, underFill, style }) {
  return (
    <svg
      viewBox="0 0 1254 1254"
      preserveAspectRatio="xMidYMid meet"
      style={style}
      aria-hidden="true"
    >
      <path fill={baseFill} d="M653.71,326.1c50.88-4.73,111.47,10.73,153.31,39.84,49.2,34.23,76.37,77.36,87.62,135.48,3.6-.59,7.21-1.1,10.83-1.52,92.88-10.79,172.27,45.67,182.75,140.57,67.04.29,124.74,56.97,125.06,124.23.17,26.08-10.11,51.14-28.55,69.59-39.15,39.06-88.77,35.63-139.2,35.65l-96.81-.02-350.76.02-312.66-.03h-82.02c-23.46.03-44.29.73-67.61-2.93-104.64-16.4-123.41-123.7-53.63-195.25,33.49-34.32,79.1-54.13,127.04-55.2.4-2.35.89-4.68,1.46-6.99,22.64-92.34,122.19-155.68,215.18-132.8,8.49-21.21,23.19-43.18,38.35-60.26,48.66-54.17,116.92-86.7,189.65-90.38Z" />
      <path fill={faceFill} d="M439.71,505.22c9.39-22.78,19.79-41.84,35.42-61.04,42.15-51.49,103.11-84.03,169.34-90.39,114.18-11.61,216.98,56.69,228.8,175.77,18.51-3.69,32.17-5.47,51-5.54,63.63-.24,118.78,40.42,133.78,102.87,3.3,13.78,3.73,25.24,3.58,39.39,15.49-1.4,28.1-1.27,43.33,2.46,27.07,6.38,50.47,23.31,64.98,47.04,24.31,40.44,14.93,86.36-26.48,110.48-32.4,18-76.07,14.38-112.36,14.38h-95.92s-298.84,0-298.84,0h-312.89s-91.37.02-91.37.02c-36.32.02-76.49,2.93-111.05-9.65-1.37-.5-7.03-3.02-7.9-3.94-58.97-27.57-46.08-104.17-5.01-141.86,36.49-33.49,73.19-45.15,121.99-44.75,1.22-8.45,3.02-16.81,5.4-25.01,11.6-41.28,39.13-76.26,76.53-97.24,41.26-22.7,82.98-26.12,127.68-13.01Z" />
      <path fill={underFill} d="M789.46,625.61c6.94-12.78,21.19-26.42,33.08-34.67,30.47-21.31,68.24-29.45,104.78-22.59,67.4,12.59,109.31,72.76,96.19,140.31,43.26-17.54,101.55-6.78,125.99,35.9,11.75,20.51,13.48,45.51,2.27,66.65-1.57,2.97-8.2,12.63-8.32,15.05-32.4,18-76.07,14.38-112.36,14.38h-95.92s-298.84,0-298.84,0h-312.89s-91.37.02-91.37.02c-36.32.02-76.49,2.93-111.05-9.65-1.37-.5-7.03-3.02-7.9-3.94-.24-2.97-6.17-11.75-7.69-15.3-11.49-26.81-.48-57.09,17.12-78.58,23.8-29.05,60.5-45.96,97.51-49.82,32.27-3.37,68.56,3.97,94.04,25.01,29.61-98.74,148.83-153.87,242.82-111.51,22.75,10.02,42.18,26.31,56.03,46.95,2.47,3.76,5.41,8.16,6.96,12.32-62.38-82.27-197.71-62.36-253.28,17.3-25.95,37.19-27.73,82.93,2.18,118.25,8.29,9.79,16.73,16.2,27.21,23.34,3.46-56.78,67.26-89.08,117.51-94.64,45.58-5.04,86.79,3.07,123.04,31.87,38.61-72.09,149.51-87.79,218.57-53.95,21.99,10.78,42.21,28.28,56.03,50.93,2.78,6.22,4.27,12.12,6.42,18.5,21.94-13.5,48-18.67,73.43-14.57,19.65,3.36,39.28,13.61,50.84,30.24,8.79,12.38,12.16,27.8,9.35,42.72,7-10.3,11.55-19.33,12.65-32.01,3.3-38.19-31.68-69.92-67.95-73,1.36-6.51,2.14-15.26,2.05-21.88-.57-29.66-12.92-57.88-34.32-78.42-22.28-21.93-52.42-34.02-83.68-33.57-32.89.67-64.15,14.47-86.8,38.33Z" />
    </svg>
  );
}

// 0 at midnight, 1 at solar noon — a smooth cosine instead of a step
// so the transition through dawn/dusk feels natural. Hours are local.
function dayFactor(hour) {
  const h = Number.isFinite(hour) ? hour : 12;
  return 0.5 + 0.5 * Math.cos(((h - 12) / 12) * Math.PI);
}
// Inline sun SVG — same shape as the asset at /public/img/weather-
// assets/sun.svg, with the three fills wired to props so the rays /
// inner / disc colors can shift with time-of-day (warm orange at
// noon, deep amber-orange at sunrise/sunset). Path data inlined so
// CSS filters aren't needed to recolor.
function SunSvg({ raysFill, innerFill, discFill, style }) {
  return (
    <svg
      viewBox="0 0 1254 1254"
      preserveAspectRatio="xMidYMid meet"
      style={style}
      aria-hidden="true"
    >
      {/* st0 — rays + outline (the long compound path) */}
      <path fill={raysFill} d="M517.02,224.14c-17.85-2.34-23.01-19.44-12.56-29.45,14.5-13.88,44.1,9.49,19.82,27.49l62.47,215.32c.34,2.06.06,2.56,1.22,3.86,23.79-4.21,44.78-5.9,68.84-2.05,2.75.44,13.85,2.95,15.06,1.91l.1-.97c14.07-50.56,27.67-101.25,40.81-152.06,5.66-21.62,11.12-44.08,17.25-65.51-7.92-5.34-13.4-13.75-8.03-23.09,2.45-4.22,6.51-7.26,11.24-8.44,21.48-5.35,31.82,28.68,3.8,32.92-1.32,8.29-4.75,20.01-6.95,28.42l-12.15,46.29c-12.23,47.11-24.86,94.12-37.88,141.02l-1.8,3.98c19.7,5.15,38.43,15.03,55.19,26.56,2.99,2.06,17.67,14.94,19.27,15.02,9.37-8.75,22.39-23.88,31.6-33.75l65.43-70.48c13.05-14.26,28.64-32.31,42.17-45.7-4.54-9.17-4.24-19.17,5.99-24.08,4.59-2.25,9.92-2.45,14.67-.57,21.6,8.56,6.72,40.67-15.17,29.49-36.98,42.05-76.45,82.46-114.24,123.83-3.59,3.93-23.74,24.65-24.88,27.22l.28,1.1c11.3,9.3,26.86,34.28,33.24,47.27,2.07,4.23,8.52,22.98,10.23,24.43l1.22-.58c63.96-20.6,129.84-42.34,194.06-61.72.39-19.27,20.5-24.86,30.51-12.15,2.64,3.35,3.78,7.63,3.16,11.85-2.31,16.58-20.47,19.7-31.53,7.01-63.75,21.27-128.98,40.38-192.69,61.54-2.41,0-2.04-.39-3.51.79,3.6,16.42,6.94,34.18,6.03,51.1-.41,8.07-1.29,16.11-2.64,24.08-.38,2.21-2.17,9.68-1.32,11.2l1.83.25,190.7,61.46c6.18-8.24,15.71-11.61,24.38-4.73,3.72,2.9,6.1,7.2,6.57,11.9,1.57,16.39-16.01,23.64-28.32,13.87-4.15-3.29-4.67-9.29-5.06-14.27-15.8-4.35-33.99-10.69-49.77-15.81l-87.96-28.42c-15.19-4.9-31.7-10.57-46.95-15.01l-7.72-2.83c-5.66,20.6-14.98,40.01-27.51,57.32-3.99,5.48-9.29,11.17-12.7,15.89-2.06,2.11-2.56,1.84-2.58,3.96,3.45,4.54,9.72,11.13,13.7,15.54l24.23,27.05,62.83,70.95c11.27,12.69,24.5,26.74,35.28,39.6,9.43-4.68,19.74-2.48,24.19,7.64,1.96,4.42,1.95,9.46,0,13.88-8.56,18.98-41.83,7.38-29.57-16.39l-91.66-103.04c-14.58-16.45-30.48-33.56-44.62-50.2-1.41.22-.86-.01-1.73.47-11.36,13.98-36.52,27.78-52.84,34.79-3.28,1.41-19.03,6.12-19.84,7.8l.54,1.98c6.18,20.79,11.87,44.79,17.58,65.96l37.97,139.73c28.3,5.38,18.51,37.46-2.48,33.35-4.73-.95-8.9-3.73-11.59-7.73-6.06-9.09-1.43-18.18,6.9-23.65-14.18-50.92-28.06-101.93-41.64-153.02l-10.73-40.22c-.91-3.36-3.13-13.45-5.15-15.3-23.41,7.34-62.43,6.31-86.12.19-1.82,1.68-10.14,33.74-11.5,38.67-5.7,20.06-11.27,40.16-16.7,60.29l-30.72,109.33c7.91,5.66,13.03,16.43,5.92,24.38-22.86,25.58-49.77-19.86-13.19-26.36l56.55-202.93c.67-1.81,1.37-3.6,2.09-5.39-23.82-5.96-55.65-23.7-72.63-41.26-.58-.25-.88-.18-1.56-.23-16.86,17.97-34.04,37.74-50.52,56.23l-88.6,99.64c4.49,9.11,3.29,19.13-6.61,23.79-4.64,2.21-10,2.35-14.74.39-21-8.72-6.41-41.24,15.81-29.18,17.22-20.79,37.93-43.01,56.02-63.36,27.56-31.33,55.42-62.4,83.57-93.2l-3.64-3.95c-19.67-22.72-33.03-45.17-40.79-74.6l-4.2,1.66-122.93,39.63c-22.03,7.14-45.23,15.13-67.35,21.65-.64,17.56-17.41,25.19-29.21,13.7-3.4-3.31-5.29-7.87-5.23-12.62.19-9.17,9.4-16.6,18.46-15.92,6.14.46,9.74,3.61,13.54,8.1,64.14-19.82,129.43-43.61,193.66-62.43.82-.13.33.01,1.36-.68-3.1-16.65-5.57-35.31-3.94-52.22.64-7.28,1.7-14.52,3.16-21.69.48-2.29,3.01-11.32,2.17-12.75l-2.32-.43c-64.42-20.95-128.96-41.53-193.61-61.75-22.43,28.53-49.05-20.37-16.62-25.15,11.27-1.66,18.32,7.1,18.72,18.32,15.66,4.33,33.52,10.45,49.17,15.33l145.88,46.53.95.57c.86-.63,8.34-19.63,9.63-22.36,8.47-18.16,20.03-34.72,34.17-48.92.28-.51.45-1.21.64-1.79-2.74-3.63-11.36-12.22-14.82-15.85-8.85-9.31-17.65-18.67-26.39-28.09-33.72-35.82-67.14-71.91-100.27-108.27-15.91,8.5-31.69-6.11-24.59-21.53,1.76-3.83,5.01-6.77,9-8.15,4.54-1.62,9.55-1.37,13.9.7,9.5,4.55,11.84,14.47,6.95,23.46,6.13,7.06,12.98,14.44,19.41,21.23,41.05,43.33,80.97,88.76,122.73,131.31-.45.04,22.21-17.34,26.14-19.81,14.97-9.18,31.16-16.21,48.08-20.88l-2.28-5.8c-1.17-6.83-4.68-17.81-6.66-24.67l-12.32-42.25c-14.1-48.12-29.33-98.19-42.21-146.52Z" />
      {/* st1 — white inner halo */}
      <path fill={innerFill} d="M622.29,451.65c90.98-3.49,167.58,67.38,171.17,158.36,3.59,90.97-67.19,167.66-158.16,171.35-91.12,3.7-167.95-67.22-171.55-158.34-3.59-91.12,67.41-167.88,158.54-171.37Z" />
      {/* st2 — orange disc + ray inserts (compound) */}
      <path fill={discFill} d="M615.12,480.62c75.16-7.51,142.12,47.46,149.4,122.64,7.28,75.18-47.89,141.98-123.09,149.04-74.88,7.03-141.34-47.86-148.59-122.72-7.25-74.86,47.43-141.48,122.27-148.96Z" />
      <path fill={discFill} d="M451.41,568.49l2.32.43c.83,1.43-1.7,10.46-2.17,12.75-1.46,7.16-2.52,14.4-3.16,21.69-1.64,16.91.84,35.57,3.94,52.22-1.02.7-.54.55-1.36.68-6.06-1.91-34.24-4.9-41.74-5.77l-82.37-9.55-196.63-22.34c-26.87-2.92-54.88-6.4-81.68-8.71,14.79-2.09,32.09-3.5,47.11-5.1l80.6-8.61,192.11-19.94c9.62-.95,78.71-5.88,83.03-7.76Z" />
      <path fill={discFill} d="M807.15,657.53l-1.83-.25c-.85-1.52.95-8.99,1.32-11.2,1.35-7.97,2.23-16.01,2.64-24.08.91-16.91-2.43-34.67-6.03-51.1,1.47-1.19,1.1-.8,3.51-.79,9.49,1.83,27.74,2.7,38.17,3.73l84,8.31,187.69,18.82c29.34,2.98,61.36,5.36,90.28,8.88l-278.36,32.84-75.76,8.77c-10.03,1.13-36.86,3.83-45.63,6.07Z" />
      <path fill={discFill} d="M626.97,41.9c2.1,13.53,3.4,30.48,4.93,44.38l8.05,70.79,22.64,204.39c1.28,11.58,6.69,73.3,9.39,78.8l-.1.97c-1.2,1.04-12.3-1.47-15.06-1.91-24.07-3.85-45.05-2.17-68.84,2.05-1.17-1.3-.88-1.8-1.22-3.86,1.95-9.82,3.26-30.87,4.33-41.97l8.27-82.66c9.31-89.84,19.42-181.1,27.62-270.98Z" />
      <path fill={discFill} d="M450.04,664.16l4.2-1.66c7.76,29.43,21.11,51.88,40.79,74.6-6.79,3.49-16.94,7.34-24.28,10.59-13.64,6.12-27.15,12.55-40.51,19.27l-196.22,100.49-86.6,44.73c6.37-6.25,17.39-15.11,24.4-21.1,19.28-16.45,38.75-32.68,58.4-48.68l153.59-125.39c12.82-10.45,25.73-20.76,38.76-30.95,5.2-4.03,24.4-18.18,27.48-21.9Z" />
      <path fill={discFill} d="M803.03,663.69l7.72,2.83-.15.98c41.55,31.17,79.28,61.73,119.59,94.49,59.96,48.68,119.31,98.1,178.03,148.27l-263.9-134.94c-23.94-12.1-47.67-24.09-72.46-34.38-2.89-1.2-6.33-2.6-9.04-4.04,3.42-4.72,8.71-10.41,12.7-15.89,12.53-17.31,21.84-36.72,27.51-57.32Z" />
      <path fill={discFill} d="M355.74,123.47c5.26,6.12,15.55,21.54,20.84,28.97l38.87,54.23,119.14,167.96c7.74,11.01,15.4,22.09,22.98,33.21,2.71,4,18.38,27.93,20.65,29.73l2.28,5.8c-16.93,4.68-33.11,11.71-48.08,20.88-3.93,2.46-26.59,19.84-26.14,19.81-.14-4.21-8-25.15-9.69-29.65-18.4-49.02-39.98-97.21-60.72-145.33l-80.11-185.61Z" />
      <path fill={discFill} d="M680.05,439.82c3.16-3.18,14.33-20.28,17.5-24.84l40.17-57.17,162.8-233.63-101.69,243.39c-13.33,31.17-25.99,62.62-37.97,94.33-1.49,3.98-7.89,20.56-8.16,23.47-1.6-.08-16.28-12.96-19.27-15.02-16.76-11.53-35.49-21.41-55.19-26.56l1.8-3.98Z" />
      <path fill={discFill} d="M585.48,790.25c23.69,6.12,62.71,7.15,86.12-.19-3.35,6.04-14.9,125.25-16.6,140.38l-13.23,113.49c-4.64,41.61-9.14,86.34-14.83,127.61-1.46-19.08-3.98-39.63-6.06-58.74l-10.93-100.42-14.97-140.03c-.88-8.43-7.22-77.81-9.52-82.1Z" />
      <path fill={discFill} d="M758.56,492.45c9.34-1.21,43.7-19.16,53.45-24.09l66.8-34.02c76.38-39.17,153.06-77.74,230.06-115.69-24.81,20.74-53.8,42.43-79.43,62.79l-147.92,118.53-55.01,44.08c-4.38,3.56-20.19,16.05-23.25,19.52l-1.22.58c-1.71-1.44-8.16-20.2-10.23-24.43-6.38-12.99-21.93-37.97-33.24-47.27Z" />
      <path fill={discFill} d="M454.95,561.76c-1.35-1.87-10.37-9.23-12.69-11.16l-34.31-28.08c-57.78-47.21-115.91-93.99-174.38-140.34l-53.88-42.39c-7.32-5.77-23.24-17.82-29.42-24.04,24.29,12.95,50.99,25.72,75.76,38.33l141.74,72.62,73.7,37.83c12.35,6.32,45.74,23.68,58.24,26.53-14.14,14.2-25.7,30.75-34.17,48.92-1.29,2.73-8.77,21.73-9.63,22.36l-.95-.57Z" />
      <path fill={discFill} d="M680.74,790.93l-.54-1.98c.8-1.69,16.56-6.4,19.84-7.8,16.32-7.01,41.49-20.81,52.84-34.79,1.22,6.99,9.13,25.87,12.19,33.53l24.89,61.7,72.86,179.66c10.12,24.67,20.22,50.39,30.64,74.84-5.06-5.8-13.96-19.32-18.6-26.01l-35.63-51.73-112.07-161.57-31.71-45.67c-2.81-4-11.7-17.12-14.7-20.18Z" />
      <path fill={discFill} d="M505.3,746.98c16.98,17.56,48.81,35.3,72.63,41.26-.72,1.79-1.42,3.58-2.09,5.39-2.5,1.1-22.23,29.92-25.12,34.03l-33.84,47.64-112.15,157.35-30.2,42.59c-3.31,4.62-9.58,14.21-13.1,18.03,11.04-24.18,21.75-51.25,32.25-75.91,25.21-59.37,50.08-118.88,74.61-178.53l23.89-57.58c3.6-8.67,11.21-25.78,13.12-34.27Z" />
    </svg>
  );
}

// Painted harvest-moon SVG — same shape as the asset at
// /public/img/weather-assets/moon.svg, three fills wired to props
// for time-of-night color shifts (warm cream at twilight → cool
// silver at deep night). Inlined so CSS filters can recolor each
// fill precisely; the mask-based crescent that lived here briefly
// was removed at the user's request.
function MoonSvg({ outerFill, innerFill, detailFill, style }) {
  return (
    <svg
      viewBox="0 0 1254 1254"
      preserveAspectRatio="xMidYMid meet"
      style={style}
      aria-hidden="true"
    >
      {/* st1 — outer crescent body */}
      <path fill={outerFill} d="M974.63,21.68l1.22.13c-3.79,2.11-19.93,8.08-24.57,9.95-14.91,6.04-29.66,12.45-44.25,19.23-174.39,81.11-361.18,237.15-382.96,440.02-15.38,146.7,41.9,317.85,131.5,433.26,102.1,131.51,294.23,217.72,458.12,237.11-96.36,17.81-201.02,32.38-298.74,23.02-17.27-1.18-38.13-4.75-55.12-7.85-109.57-19.8-211-73.35-299.05-139.3-144.4-108.17-248.27-261.9-243.53-447.65.5-94.68,26.29-187.78,85.18-263.6,117.76-151.63,316.32-214.17,493.76-265.25,56.77-16.34,120.56-31.39,178.44-39.07Z" />
      {/* st2 — inner highlight (brighter cream) */}
      <path fill={innerFill} d="M874.66,50.87l1.18-.09.14.44c-2.36,1.79-5.89,3.25-8.63,4.47-11.67,5.18-22.86,10.92-34.15,16.86-28.03,14.89-55.27,31.24-81.58,48.99-115.57,79.06-218.81,193.93-245.43,335.31-5.29,28.32-7.87,57.07-7.71,85.88,1.11,118.53,47.7,258.78,114.43,356.36,18.3,27.14,38.95,52.62,61.72,76.14,95.13,97.93,225.68,160.02,358.96,185.16-83.26,11.55-164.35,15.79-247.85,3.58-9.41-.92-26.08-4.49-35.28-6.46-58.24-12.46-114.68-34.5-167.02-62.82-145.83-78.89-271.17-198.92-319.47-361.38-33.62-113.07-27.53-240.8,28.15-345.84,70.89-133.72,201.17-206.49,337.63-259.38,42.45-16.35,85.44-31.24,128.91-44.66,21.56-6.56,43.22-12.8,64.97-18.71,16.72-4.52,34.58-8.88,51.05-13.86Z" />
      {/* st0 — surface craters / details */}
      <path fill={detailFill} d="M483.8,787.09c-6.74-3.31-3.32-9.7-5.26-14.37-28.52-19.86-57.08-40.57-86.04-59.74,20.65,66.89,46.19,133.26,68.69,199.49,20.62,1.27,9.48,22.22-.3,19.26-8.54-2.59-10.05-11.34-1.88-18.53-21.24-52.24-42.16-104.62-62.76-157.12-5.08-12.92-10.55-25.79-15.5-38.69-2.54,17.62-6.48,36.87-9.63,54.55-3.01,16.85-5.89,36.08-9.46,52.71l-3.25,18.89c-.46,3.84,2.89,4.2,4.81,7.45,4.37,7.36-3.43,15.59-11.33,13.18-5.62-1.72-7.9-7-6.23-12.41,1.13-3.66,5.91-5.3,9.13-6.68l.68-1.88c.55-6.71,1.7-14.61,2.54-21.37,2.22-25.82,5.81-51.62,8.57-77.39,1.04-9.66,3.41-21.02,2.67-30.62-.08-.95-1.02-1.6-1.78-2.12-2.93-1.99-41.49,12.22-46.87,14.14-10.11,3.61-5.19,2.6-9.59,10.94-1.12,2.1-2.94,3.17-5.21,3.71-2.67.62-5.52.2-7.8-1.37-2.13-1.46-3.29-3.74-3.73-6.23-.44-2.44-.3-5.12,1.2-7.19,1.7-2.34,4.57-3.24,7.33-3.48,3.77-.33,5.98,1.1,8.76,3.46l31.04-16.01c4.98-2.57,13.63-6.65,17.95-9.71.6-2.32.47-2.69.29-5.14-3.76-3.67-18.09-13.46-22.89-16.89l-51.74-36.59c-17.3,13.17-22.59-17.05-6.28-16,7.25.47,9.2,6.64,7.94,13.28,6.85,4.6,75.81,44.91,78.18,44.93l1.41-1.4c.27-3.36-33.01-103.7-36.48-114.52-2.36-6.46-4.32-13.52-6.29-20.13-6.44-.16-12.45-3.58-11.84-10.27,1.44-15.8,31.89-6.56,14.11,9.24,2.56,6.3,4.94,12.14,7.16,18.58,15.55,36.47,28.65,74.64,44.74,110.94.53,1.2,1.01,1.73,2.19,2.3,1.81.01,1.82.13,3.43-.61,5.25-8.2,13.19-25.2,18.02-34.8l34.97-69.74c-2.04-2.32-4.54-5.11-4.15-8.41.67-5.64,4.5-10.44,10.51-9.12,3.14.71,5.83,2.73,7.38,5.56,4.87,8.82-3.18,13.45-11.34,13.16-11.1,26.93-33.48,80.47-44.35,109.97.24.61.47,1.22.71,1.83l1.6,1.31c6.81-.81,25.87-5.85,33.25-7.87,13.79-3.78,31.17-7.33,44.58-11.52,1.6-16.75,21.62-13.69,18.3.97-2.24,9.89-11.33,9.15-17.32,1.87-16.54,6.03-33.02,12.21-49.45,18.53-6.69,2.53-20.34,7.23-26.21,10.26-.38,2.1-.45,1.99-.01,4.06,7.6,7.64,19.62,17.42,28.03,24.52,17.3,14.74,34.93,29.08,52.88,43.01,21.63-11.99,18.54,23.96,1.92,15.82Z" />
      <path fill={detailFill} d="M456.69,273.37c-14.7-15.97,16.21-20.53,11.84-4.71-1.31,4.74-8.64,5-9.49,7.54-6.44,19.03-11.27,38.62-16.87,57.91-2.24,7.71-5.43,16.82-6.22,24.66l.36.84c4.03.66,38.08-12.26,43.77-14.84,1.71-3.69,1.17-11.41,9.21-9.21,2.68.76,4.93,2.61,6.18,5.1,2.19,4.38-.1,7.8-4.43,9.39-4.35,1.6-6.01.13-9.3-2.62-8.95,4.16-35.2,16.68-43.01,22.07,10.24,5.82,26.52,12.13,37.74,16.87,7.1-6.42,15.29-2.53,13.7,5.42-.4,1.98-1.59,3.72-3.3,4.8-9.7,6.12-9.95-6.01-12.03-7.33-5.21-3.32-32.5-9.82-38.26-11.17,3.78,7.52,25.75,50.03,30.59,51.79,2.63.96,6.63.87,8.01,3.74.93,1.95.66,5-.2,6.95-.79,1.79-2.39,3.12-4.22,3.75-2.07.72-4.54.56-6.49-.48-1.75-.92-2.99-2.57-3.39-4.5-.71-3.32.66-5.45,2.54-8.01-10.43-14.52-23.72-35.7-34.55-48.94-5.7,21.5-11.06,43.02-16.14,64.67-1.97,8.41-4.47,16.77-5.74,25.31.11,1.21,1.32,2.54,1.98,3.33,1.48,1.61,2.27,2.96,2.23,5.18-.15,7.57-11.45,10.02-15.08,3.7-5.59-9.7,7.19-10.82,8.07-15.46,1.81-9.57,3.51-19.05,5.21-28.46l10.32-58.89c-11.97,9.33-28.99,24.69-40.29,35.2,8.43,12.35-17.5,17.5-13.63,2.46,1.55-6.03,6.37-6.32,11.59-4.87,4.38-4.33,36.56-37.16,36.09-41.48-4.64-3.38-42.57-11.45-50.55-13.14-1.82,4.33-6.53,7.46-10.85,4.07-2.17-1.69-3.52-4.21-3.73-6.95-.37-5.05,3.23-7,7.69-7.36,6.23-.5,5.73,5.88,8.71,7.5,10.96,1.62,21.93,3.09,32.93,4.42,4.7.58,13.79,2.7,17.8.6,1.2-1.92,1.56-3.43.8-5.65-1.97-5.74-12.07-37.3-14.34-40.36-3.62-2.18-12.76-5.25-9.93-11.24,4.74-10.02,23.92-5.48,12.82,8.39l-.43.53c2.8,6.75,16.92,37.58,20.58,42.52l.87.12c3.64-3.46,27.61-73.76,30.85-83.13Z" />
      <path fill={detailFill} d="M618.79,979.57c-1.15,1.69-2.98,2.78-5,3.01-1.89.24-3.8-.33-5.25-1.56-1.01-.86-1.79-1.99-2.56-3.05-7.42.76-30.88,3.2-36.47,6.09-1.78,3.03,1.35,9.02,2.71,12.15,4.72,10.81,9.51,21.6,14.49,32.29,18.78.73,6.08,22.39-4.32,10.64-2.21-2.49-.13-7.1,1.84-9.31-1.71-3.18-22.07-42.53-25.66-38.41-3.78,4.33-9.42,19.77-12.33,25.92-1.63,3.45-3.3,7.62-4.67,11.2,5.66,6.4,3.44,13.96-4.91,14.08-3.82.06-7.46-5.52-6.12-9.43,2.34-6.81,7.48-2.34,9.21-8.89,2.15-8.12,11.17-29.56,10.33-36.77-4.47-.52-31.54,7.65-36.66,9.95-2.18,3.7-3.95,8.2-8.02,9.34-12.6-1.19-6.5-23.63,6.16-12.01,5.61-2.24,32.48-12.56,35.43-16.45-.1-6.06-16.45-18-20.75-22.8-1.44-1.6-6.88-6.26-8.67-7.47-11.69,7.93-18.04-13.46-2.17-11.75,4.72.51,5,5.75,3.92,9.48,8.66,6.58,17.47,12.98,26.1,19.61,2.26,1.73,4.7,3.8,7.52,2.78,2.39-3.51,1.59-33.78,2.25-41.28l.07-.76c-.05-4.98-.06-9.26.21-14.22-.05-7.6-4.68-5.09-6.09-10.57-2.62-10.17,13.41-11.79,14.22-2.66.39,4.35-5.22,6.71-6.19,11.73,1.19,5.55,1.51,16.67,1.7,22.57l.09.65c.79,5.91,1.43,31.81,3.35,34.49,3.87-.52,18.78-17.5,22.22-21.19,2.32-2.5,1.27-2.62,1.15-5.98-.09-2.5-.45-3.06,1.46-5.21,1.46-1.63,3.45-2.48,5.64-2.36,1.95.1,3.82,1.19,4.98,2.75,1.17,1.59,1.81,3.59,1.4,5.59-.51,2.49-1.55,3.38-3.35,4.78-2.16,1.67-6.57.28-6.83.54-4.6,4.55-17.19,21.94-19.53,26.34l.24,1.32c3.14,1.73,30.1.68,34.92.21,3.22-1.61,3.63-8.41,10.29-5.98,4.45,1.61,6.24,6.74,3.65,10.6Z" />
    </svg>
  );
}

// Moon palette per time-of-night. Warm cream at twilight (matches
// the painted asset's default tones), cooler silver at deep midnight
// when the moon reads more clinically. Lerp via `moonCoolness(hour)`
// — 0 at twilight (6pm / 6am), 1 at midnight.
const MOON_TWILIGHT_OUTER  = [234, 214, 173];  // #ead6ad
const MOON_TWILIGHT_INNER  = [246, 233, 205];  // #f6e9cd
const MOON_TWILIGHT_DETAIL = [216, 194, 149];  // #d8c295
const MOON_DEEP_OUTER      = [218, 222, 235];
const MOON_DEEP_INNER      = [238, 242, 250];
const MOON_DEEP_DETAIL     = [192, 200, 215];

// Where the moon sits in the upper strip per hour. Visible 6pm-6am
// (mirrors the sun's 6am-6pm window) — we wrap night hours into a
// continuous 0-12 range so position math is the same as the sun:
//   18:00 → 0   (moon rising at left)
//   00:00 → 6   (moon at top, peak high)
//   06:00 → 12  (moon setting at right)
function moonPosition(hour) {
  let nh;
  if (hour >= 18) nh = hour - 18;            // 18-24 → 0-6
  else if (hour <= 6) nh = hour + 6;         // 0-6  → 6-12
  else return null;                          // daylight, no moon
  const left = (nh / 12) * 100;
  const fromMidnight = Math.abs(nh - 6);     // 0 at midnight, 6 at edges
  const lowness = (fromMidnight / 6) ** 2;
  const top = 2 + lowness * 22;
  return { left, top };
}
function moonCoolness(hour) {
  // Same shape as moonPosition's lowness inverse: 1 at midnight,
  // 0 at the dusk/dawn edges. Pulls the palette toward silver in
  // the dead of night and stays warm-cream at twilight.
  let nh;
  if (hour >= 18) nh = hour - 18;
  else if (hour <= 6) nh = hour + 6;
  else return 0;
  const fromMidnight = Math.abs(nh - 6);
  return 1 - (fromMidnight / 6);
}

// Sun palette per time-of-day. Rays + disc shift toward deep amber-
// brown at sunrise/sunset; mid-day stays bright golden. Inner halo
// always near-white at noon, warming to a tan glow as the sun gets
// low. The asset's painted ray-color (#f7ac25) is the midday default.
const SUN_DAY_RAYS  = [247, 172, 37];   // #f7ac25 — golden midday
const SUN_DAY_DISC  = [247, 172, 37];
const SUN_DAY_INNER = [255, 255, 255];
const SUN_LOW_RAYS  = [178, 92, 30];    // deep amber-brown rays
const SUN_LOW_DISC  = [148, 65, 22];    // dark brown-orange disc
const SUN_LOW_INNER = [240, 200, 145];  // warm tan inner halo

// "Lowness" of the sun in the sky based on hour. 0 at noon, 1 at
// horizon-grazing (~6am or ~6pm). Stays near 0 across the broad
// midday and ramps quickly in the last hour or two of daylight, so
// the amber-brown sunset palette only kicks in when the sun is
// genuinely near the horizon.
function sunLowness(hour) {
  // Quadratic so noon stays bright golden longer, then dives to
  // amber as the hour approaches sunset/sunrise.
  const t = Math.min(1, Math.max(0, Math.abs(hour - 12) / 6));
  return t * t;
}

// Where the sun sits in the upper strip per hour. Horizontal: linear
// 6am=0% → 12pm=50% → 6pm=100%. Vertical: peaks (highest in sky)
// at noon, drops toward horizon at the edges of daylight via the
// same lowness curve.
function sunPosition(hour) {
  // Hidden outside 5am-7pm — clear-sky night gets nothing for now.
  if (hour < 5 || hour > 19) return null;
  const left = ((hour - 6) / 12) * 100;
  const low  = sunLowness(hour);
  const top  = 2 + low * 26;             // 2% at noon, ~28% at sunset
  return { left, top };
}

function lerp(a, b, t) { return a + (b - a) * t; }
function lerpVeil(endpoints, t) {
  const [r1,g1,b1,a1] = endpoints.day;
  const [r2,g2,b2,a2] = endpoints.night;
  return `rgba(${Math.round(lerp(r1,r2,1-t))}, ${Math.round(lerp(g1,g2,1-t))}, ${Math.round(lerp(b1,b2,1-t))}, ${lerp(a1,a2,1-t).toFixed(3)})`;
}
function lerpRgb(c1, c2, t) {
  return [
    Math.round(lerp(c1[0], c2[0], t)),
    Math.round(lerp(c1[1], c2[1], t)),
    Math.round(lerp(c1[2], c2[2], t)),
  ];
}

// Pre-generate particle props so the random seed is stable across
// re-renders. Without this, every state change in the parent would
// reroll the rain pattern and produce a visible "blink."
function buildParticles(condition) {
  const count = COUNTS[condition] || 0;
  if (count === 0) return [];
  const isSnow = condition === "snow";
  const out = [];
  for (let i = 0; i < count; i++) {
    out.push({
      id: i,
      left:     Math.random() * 100,                          // vw %
      delay:    Math.random() * (isSnow ? 8 : 2.4),           // s
      duration: isSnow
        ? 6 + Math.random() * 6                               // 6-12s slow drift
        : 0.8 + Math.random() * 0.8,                          // 0.8-1.6s fast streak
      opacity:  isSnow
        ? 0.55 + Math.random() * 0.35
        : 0.35 + Math.random() * 0.40,
      length:   isSnow
        ? 4 + Math.random() * 4                               // px (snowflake size)
        : 14 + Math.random() * 12,                            // px (rain streak length)
      drift:    isSnow ? (Math.random() - 0.5) * 60 : 0,      // px horizontal sway for snow
    });
  }
  return out;
}

export default function WeatherFX() {
  const { weather } = useWeather();
  // Subscribe to override changes so the admin's button instantly
  // reflects in this tab without a refresh.
  const [override, setOverride] = useState(() => getWeatherFxOverride());
  useEffect(() => {
    const onChange = () => setOverride(getWeatherFxOverride());
    window.addEventListener("weatherFx:override-changed", onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener("weatherFx:override-changed", onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const realCondition = weather?.condition;
  const condition = override || realCondition;

  // Map storm to its own treatment but reuse rain density underneath
  // so a thunderclap doesn't flash through an empty sky. "clear" gets
  // its own bucket — it doesn't render rain or clouds, but it DOES
  // render the sun (and only the sun).
  const fxKind = condition === "drizzle" ? "drizzle"
              : condition === "rain"     ? "rain"
              : condition === "storm"    ? "storm"
              : condition === "snow"     ? "snow"
              : condition === "cloudy"   ? "cloudy"
              : condition === "clear"    ? "clear"
              : null;

  // Memo keyed on the bucket name only — particles re-roll only when
  // the weather actually transitions (rain → snow), not on every parent
  // re-render.
  const particles = useMemo(() => buildParticles(fxKind), [fxKind]);
  // Clouds render on every wet condition (drizzle / rain / storm) and
  // on cloudy itself — rain from a clear sky breaks the illusion. The
  // CLOUD_COUNTS map sets density per condition, with storm densest.
  // Re-rolls on the same cadence as particles, so a transition into
  // wet weather produces a fresh drift pattern.
  const clouds = useMemo(() => buildClouds(fxKind), [fxKind]);

  // Hour-of-day for time-aware veil + cloud color. Refreshes every
  // 15 min so an open tab transitions through dawn / dusk without
  // requiring a reload. setInterval is cheap; we only mount this
  // component once.
  const [hourTick, setHourTick] = useState(() => new Date().getHours() + new Date().getMinutes() / 60);
  useEffect(() => {
    const id = setInterval(() => {
      setHourTick(new Date().getHours() + new Date().getMinutes() / 60);
    }, 15 * 60 * 1000);
    return () => clearInterval(id);
  }, []);
  const t = dayFactor(hourTick);          // 0=midnight, 1=noon
  const veilEndpoints = fxKind ? VEIL_ENDPOINTS[fxKind] : null;
  const veilColor = veilEndpoints ? lerpVeil(veilEndpoints, t) : null;
  const veilBlend = fxKind === "snow" ? "screen" : "multiply";
  // Per-condition cloud fills, lerped between day and night palettes.
  // Three rgb triplets feed the inline SVG's three layered paths
  // (base / face / under) so each weather state has its own palette.
  const cloudFills = fxKind && fxKind !== "clear"
    ? cloudFillsFor(fxKind, t)
    : null;
  // Sun position + fills. Only computed when condition is "clear" —
  // sun never co-renders with clouds/rain/storm. Returns null when
  // the hour is outside daylight (5am-7pm); the JSX guards on null
  // so a clear-night sky falls through to the moon instead.
  // Sun + moon now render across ALL conditions (not just "clear").
  // During cloud-bearing weather (cloudy / rain / storm / drizzle)
  // they paint UNDER the clouds and get a veil filter applied so
  // they look "behind" the cover instead of overlaid on top.
  const sunPos = sunPosition(hourTick);
  const sunLow = sunPos ? sunLowness(hourTick) : 0;
  const sunRays  = sunPos ? rgbStr(lerpRgb(SUN_DAY_RAYS,  SUN_LOW_RAYS,  sunLow)) : null;
  const sunDisc  = sunPos ? rgbStr(lerpRgb(SUN_DAY_DISC,  SUN_LOW_DISC,  sunLow)) : null;
  const sunInner = sunPos ? rgbStr(lerpRgb(SUN_DAY_INNER, SUN_LOW_INNER, sunLow)) : null;
  // Moon — same arc inverted so it peaks at midnight, dips at the
  // dusk/dawn edges. Color shifts warm cream → cool silver via
  // `moonCoolness`. Goes BEHIND clouds same as the sun.
  const moonPos = moonPosition(hourTick);
  const moonCool = moonPos ? moonCoolness(hourTick) : 0;
  const moonOuter  = moonPos ? rgbStr(lerpRgb(MOON_TWILIGHT_OUTER,  MOON_DEEP_OUTER,  moonCool)) : null;
  const moonInner  = moonPos ? rgbStr(lerpRgb(MOON_TWILIGHT_INNER,  MOON_DEEP_INNER,  moonCool)) : null;
  const moonDetail = moonPos ? rgbStr(lerpRgb(MOON_TWILIGHT_DETAIL, MOON_DEEP_DETAIL, moonCool)) : null;

  // Veil filter — applied to sun/moon when there's cloud cover
  // overhead so they read as "peeking through" rather than overlaid
  // on top. Sun gets MORE aggressive desaturation than moon (sun
  // is bright-warm; moon is already cool, so the filter doesn't
  // need to fight as much hue).
  const sunFilterByKind = {
    cloudy:  "saturate(0.40) brightness(0.65)",
    rain:    "saturate(0.30) brightness(0.55)",
    storm:   "saturate(0.18) brightness(0.40)",
    drizzle: "saturate(0.50) brightness(0.75)",
    snow:    "saturate(0.55) brightness(0.85)",
  };
  const moonFilterByKind = {
    cloudy:  "saturate(0.75) brightness(0.85)",
    rain:    "saturate(0.65) brightness(0.78)",
    storm:   "saturate(0.50) brightness(0.65)",
    drizzle: "saturate(0.85) brightness(0.92)",
    snow:    "saturate(0.85) brightness(0.92)",
  };
  const sunFilter  = sunFilterByKind[fxKind]  || "none";
  const moonFilter = moonFilterByKind[fxKind] || "none";

  if (!fxKind) return null;

  const isSnow = fxKind === "snow";

  return (
    <>
      <style>{`
        @keyframes weatherFxRain {
          0%   { transform: translate3d(0, -10vh, 0); }
          100% { transform: translate3d(0, 110vh, 0); }
        }
        @keyframes weatherFxSnow {
          0%   { transform: translate3d(0, -10vh, 0)  rotate(0deg); }
          100% { transform: translate3d(var(--drift), 110vh, 0) rotate(360deg); }
        }
        @keyframes weatherFxLightning {
          0%, 91%, 100% { background-color: transparent; }
          92%, 92.7%    { background-color: rgba(255,255,240,0.18); }
          93%, 93.4%    { background-color: transparent; }
          93.5%, 94.2%  { background-color: rgba(255,255,240,0.28); }
          94.3%         { background-color: transparent; }
        }
        @keyframes weatherFxCloudRight {
          0%   { transform: translate3d(-40vw, 0, 0); }
          100% { transform: translate3d(140vw, 0, 0); }
        }
        @keyframes weatherFxCloudLeft {
          0%   { transform: translate3d(140vw, 0, 0); }
          100% { transform: translate3d(-40vw, 0, 0); }
        }
      `}</style>

      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          zIndex: 0,
          overflow: "hidden",
        }}
      >
        {/* Atmospheric veil — sits below the particles so rain streaks
            and clouds render on top of the dimmed sky. Multiply blend
            with a slate tint pulls the cream page bg down toward
            overcast without flattening the UI's cards (cards have
            their own opaque fill, which the multiply acts on
            independently of text legibility).
            Color is time-of-day aware: midday cloudy reads as a
            bright cool gray (real overcast); midnight cloudy reads
            as deep slate. See VEIL_ENDPOINTS / dayFactor above. */}
        {veilColor && (
          <div style={{
            position: "absolute",
            inset: 0,
            background: veilColor,
            mixBlendMode: veilBlend,
          }} />
        )}

        {particles.map(p => (
          <div
            key={p.id}
            style={{
              position: "absolute",
              top: 0,
              left: `${p.left}%`,
              width: isSnow ? p.length : 1.5,
              height: p.length,
              background: isSnow
                ? "radial-gradient(circle, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.55) 40%, rgba(255,255,255,0) 70%)"
                : "linear-gradient(to bottom, rgba(170,200,230,0) 0%, rgba(170,200,230,0.85) 50%, rgba(170,200,230,0) 100%)",
              borderRadius: isSnow ? "50%" : 1,
              opacity: p.opacity,
              animationName: isSnow ? "weatherFxSnow" : "weatherFxRain",
              animationDuration: `${p.duration}s`,
              animationDelay: `${p.delay}s`,
              animationIterationCount: "infinite",
              animationTimingFunction: isSnow ? "linear" : "cubic-bezier(.55,0,.85,.55)",
              willChange: "transform",
              "--drift": `${p.drift}px`,
            }}
          />
        ))}

        {/* Lightning is a full-viewport veil that flashes once per
            ~12s animation cycle. Sits above the rain so the whole
            sky goes briefly bright. */}
        {fxKind === "storm" && (
          <div style={{
            position: "absolute",
            inset: 0,
            animation: "weatherFxLightning 12s infinite",
            mixBlendMode: "screen",
          }} />
        )}

        {/* Sun — paints BEFORE the clouds so cloud sprites cover it
            visually. Renders during daylight (5am-7pm) regardless of
            condition; veil filter (sunFilter) desaturates + dims it
            when there's cloud cover so it reads as the sun "behind"
            the clouds rather than overlaid on top. Position tracks
            horizontal across the viewport (left at sunrise → center
            at noon → right at sunset) and dips toward horizon at the
            edges of daylight via sunPosition(). Color shifts from
            bright golden midday to deep amber-brown at sunset. */}
        {sunPos && (
          <div
            style={{
              position: "absolute",
              left: `${sunPos.left}%`,
              top: `${sunPos.top}%`,
              transform: "translate(-50%, 0)",
              width: 190,
              height: 190,
              opacity: 0.92,
              // blur for soft edge + condition-aware veil for "behind
              // clouds" feel.
              filter: `blur(0.5px) ${sunFilter}`,
              WebkitFilter: `blur(0.5px) ${sunFilter}`,
              pointerEvents: "none",
              willChange: "transform",
            }}
          >
            <SunSvg
              raysFill={sunRays}
              innerFill={sunInner}
              discFill={sunDisc}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          </div>
        )}

        {/* Moon — same "behind clouds" rule as the sun, lighter veil
            since the moon is already cool-toned and doesn't fight
            the cloud color as hard. Renders 6pm-6am regardless of
            condition; clear-night gets full color, cloudy/rain/storm
            get progressively heavier desaturation. CrescentMoon —
            the original MCM-styled mask-based crescent (not the
            painted harvest-moon asset). */}
        {moonPos && (
          <div
            style={{
              position: "absolute",
              left: `${moonPos.left}%`,
              top: `${moonPos.top}%`,
              transform: "translate(-50%, 0)",
              width: 170,
              height: 170,
              opacity: 0.88,
              filter: `blur(0.4px) ${moonFilter}`,
              WebkitFilter: `blur(0.4px) ${moonFilter}`,
              pointerEvents: "none",
              willChange: "transform",
            }}
          >
            <MoonSvg
              outerFill={moonOuter}
              innerFill={moonInner}
              detailFill={moonDetail}
              style={{
                width: "100%",
                height: "100%",
                display: "block",
              }}
            />
          </div>
        )}

        {/* Drifting cloud sprites — designed cumulus SVG (three layered
            fills: base / face / under) inlined as <CloudSvg> so the
            three colors can be driven per-condition. Each sprite has
            its own bounding box (sized in buildClouds with
            CLOUD_ASPECT) and drifts horizontally via a CSS keyframe.
            Each render reads the active palette via cloudFills (lerp
            of day/night endpoints by hour-of-day). Sits AFTER the
            sun/moon block so clouds visually cover them. */}
        {cloudFills && clouds.map(c => {
          // Gentle blur softens the SVG's hard painted edges so it
          // doesn't read as a sticker. drop-shadow() was removed —
          // stacking it on every animating cloud thrashed the GPU
          // compositor (each frame re-rasterized + blurred each
          // sprite into a shadow buffer). The cloud's own three-
          // layer SVG carries enough contrast on its own.
          const blurPx = Math.max(1.0, Math.min(2.6, c.width / 280));
          const filterChain = `blur(${blurPx}px)`;
          return (
            <div
              key={c.id}
              style={{
                position: "absolute",
                top: `${c.top}%`,
                left: 0,
                width: c.width,
                height: c.height,
                opacity: c.opacity,
                // Filter chain: gentle blur softens the hard SVG edge
                // (the painted asset reads as a sticker without it),
                // then drop-shadow paints the bottom-cast shadow in
                // the state-tinted color so storm clouds feel heavy
                // and cloudy-day clouds feel light.
                filter: filterChain,
                WebkitFilter: filterChain,
                animationName: c.goesRight ? "weatherFxCloudRight" : "weatherFxCloudLeft",
                animationDuration: `${c.duration}s`,
                animationDelay: `${c.delay}s`,
                animationIterationCount: "infinite",
                animationTimingFunction: "linear",
                willChange: "transform",
                pointerEvents: "none",
              }}
            >
              <CloudSvg
                baseFill={rgbStr(cloudFills.base)}
                faceFill={rgbStr(cloudFills.face)}
                underFill={rgbStr(cloudFills.under)}
                style={{
                  width: "100%",
                  height: "100%",
                  display: "block",
                  // Crop the empty top/bottom of the square viewBox so
                  // the visible cloud body fills the (wider-than-tall)
                  // bounding box. xMidYMid meet + a tighter container
                  // width vs. height does the trim implicitly.
                  overflow: "visible",
                }}
              />
            </div>
          );
        })}

      </div>
    </>
  );
}
